import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

interface MiniVideoPlayerProps {
  slotId: string;
  s3Url?: string;
  seekValue?: string; // Seek time in seconds as string
  duration?: string; // Duration in seconds as string
  previewTime?: number; // Added for scrubbing preview
  onClick: (slotId: string) => void;
  style?: React.CSSProperties; // For positioning
  song?: string; // Added prop for song name
  artist?: string; // Added prop for artist name
  currentFormat?: 'horizontal' | 'vertical'; // Added for tease border selection
  horizontalPosition?: number; // Horizontal position for vertical format (-50 to 50)
  verticalPosition?: number; // Vertical position for vertical format (-50 to 50)
  isPreviewMode?: boolean; // Make progress bars red during preview
  teaseBorderUrl?: string; // URL for tease border overlay image
}

export const MiniVideoPlayer: React.FC<MiniVideoPlayerProps> = ({
  slotId,
  s3Url,
  seekValue = '0', // Default seek to 0 if undefined
  duration = '10', // Default duration to 10 seconds if undefined
  previewTime, // New prop for scrubbing
  onClick,
  style,
  song,
  artist,
  currentFormat = 'horizontal', // Default to horizontal
  horizontalPosition = 0, // Default to center
  verticalPosition = 0, // Default vertical position to center
  isPreviewMode = false, // Default to normal mode
  teaseBorderUrl // URL for tease border overlay
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLoopTime, setCurrentLoopTime] = useState<number>(0); // Track position within duration loop
  const intervalRef = useRef<NodeJS.Timeout | null>(null); // Ref for interval

  const startTime = parseFloat(seekValue);
  const videoDuration = parseFloat(duration);
  const endTime = startTime + videoDuration;

  // Effect for normal video playback
  useEffect(() => {
    const video = videoRef.current;
    
    // Debug logging specifically for outro_video3
    if (slotId === 'outro_video3') {
      console.log(`[DEBUG outro_video3] Effect triggered with:`, {
        hasVideo: !!video,
        s3Url: s3Url,
        urlLength: s3Url?.length,
        seekValue: seekValue,
        previewTime: previewTime
      });
    }
    
    if (!video || !s3Url) {
      setIsPlaying(false);
      if (intervalRef.current) clearInterval(intervalRef.current); // Clear interval if no video
      if (slotId === 'outro_video3') {
        console.log(`[DEBUG outro_video3] Early return - hasVideo: ${!!video}, hasUrl: ${!!s3Url}`);
      }
      return;
    }

    // --- Reset state on prop change ---
    setError(null);
    setIsPlaying(false);
    if (intervalRef.current) clearInterval(intervalRef.current);

    // --- Event Handlers ---
    const handleLoadedMetadata = () => {
      console.log(`[MiniPlayer ${slotId}] Metadata loaded. Seeking to ${startTime}`);
      // Only set to startTime if no previewTime is active
      if (previewTime === undefined) {
        video.currentTime = startTime;
      }
    };

    const handleCanPlay = async () => {
      // Only start playing if no previewTime is active
      if (previewTime === undefined) {
        console.log(`[MiniPlayer ${slotId}] Can play. Attempting autoplay...`);
        try {
          await video.play();
          setIsPlaying(true);
          console.log(`[MiniPlayer ${slotId}] Autoplay started.`);
        } catch (playError) {
          console.error(`[MiniPlayer ${slotId}] Autoplay failed:`, playError);
          setError('Autoplay blocked'); // Inform user if needed
          setIsPlaying(false);
        }
      }
    };

    const handleError = (e: Event) => {
      console.error(`[MiniPlayer ${slotId}] Video error:`, e);
      console.error(`[MiniPlayer ${slotId}] Failed URL:`, s3Url);
      // Check if video element has more error details
      if (video && video.error) {
        console.error(`[MiniPlayer ${slotId}] Video element error code:`, video.error.code);
        console.error(`[MiniPlayer ${slotId}] Video element error message:`, video.error.message);
        
        // Provide more detailed error information
        const errorMessages = {
          1: 'MEDIA_ERR_ABORTED - The video download was aborted',
          2: 'MEDIA_ERR_NETWORK - A network error occurred while downloading',
          3: 'MEDIA_ERR_DECODE - An error occurred while decoding the video',
          4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - The video format is not supported'
        };
        console.error(`[MiniPlayer ${slotId}] Error details:`, errorMessages[video.error.code as keyof typeof errorMessages] || 'Unknown error');
      }
      setError('Video load error');
      setIsPlaying(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };

    // --- Add Listeners ---
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // --- Cleanup ---
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Pause video on unmount
      if (video && !video.paused) {
         video.pause();
      }
    };

  }, [s3Url, startTime, slotId, previewTime]); // Re-run if URL, start time, or previewTime changes

  // Effect for scrubbing preview
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !s3Url) return;

    // If preview time is set, update video time and pause playback
    if (previewTime !== undefined) {
      // Pause any playback
      if (!video.paused) {
        video.pause();
        setIsPlaying(false);
      }
      
      // Set to preview time if the video is ready
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA or better
        video.currentTime = previewTime;
      }
    } else if (video.paused) {
      // When preview ends (previewTime becomes undefined), 
      // start playing from current position or startTime
      
      // Make sure we're at the correct time position (using either current time or startTime)
      // This ensures if we're at a random scrubbed position, we use that as the start time
      // If no scrubbing happened, we use the original startTime
      const loopStartTime = video.currentTime; // Use current position as loop start
      
      video.play()
        .then(() => {
          setIsPlaying(true);
          console.log(`[MiniPlayer ${slotId}] Playback started after preview at time ${loopStartTime}`);
        })
        .catch(e => console.error(`[MiniPlayer ${slotId}] Error starting playback after preview:`, e));
    }
  }, [previewTime, s3Url, slotId, startTime]);

  // --- Loop Logic --- 
  useEffect(() => {
    const video = videoRef.current;
    // Only loop when playing
    if (isPlaying && video) {
      console.log(`[MiniPlayer ${slotId}] Starting loop interval. Seek position: ${startTime}`);
      
      // When the player starts or preview changes, update the loop boundaries
      // Use current video position as the loop start (works after scrubbing)
      const loopStart = video.currentTime;
      const loopEnd = loopStart + videoDuration;
      
      console.log(`[MiniPlayer ${slotId}] Loop set from ${loopStart} to ${loopEnd} for ${slotId}`);
      
      // Clear any existing interval before setting a new one
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        // Check if video is still available
        if (!video || video.readyState < 2) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          return;
        }
        
        // Update current loop time (0-duration seconds within the loop)
        const currentVideoTime = video.currentTime;
        const relativeTime = currentVideoTime - loopStart;
        setCurrentLoopTime(Math.max(0, Math.min(videoDuration, relativeTime)));
        
        if (currentVideoTime >= loopEnd) {
          console.log(`[MiniPlayer ${slotId}] Loop end reached (${currentVideoTime.toFixed(2)} >= ${loopEnd.toFixed(2)}). Resetting to ${loopStart.toFixed(2)}`);
          video.currentTime = loopStart;
          setCurrentLoopTime(0); // Reset loop time display
          // Ensure it keeps playing after reset
          if (video.paused) {
            video.play().catch(e => console.error(`[MiniPlayer ${slotId}] Error restarting play in loop:`, e));
          }
        }
      }, 100); // Check every 100ms
    } else {
      // Clear interval if not playing
      if (intervalRef.current) {
        console.log(`[MiniPlayer ${slotId}] Clearing loop interval.`);
        clearInterval(intervalRef.current);
      }
    }

    // Cleanup interval on isPlaying change or unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, slotId, startTime, previewTime, seekValue]); // Include seekValue in dependencies

  // Create tooltip content based on available information
  const getTooltipContent = () => {
    // Replace || delimiter with space for display
    const displaySong = song?.replace(/\|\|/g, ' ');
    const displayArtist = artist?.replace(/\|\|/g, ' ');

    if (displaySong && displayArtist) {
      return `${displaySong} - ${displayArtist}`;
    } else if (displaySong) {
      return displaySong;
    } else if (displayArtist) {
      return displayArtist;
    } else if (s3Url) {
      // Extract filename from URL as fallback
      const filename = s3Url.split('/').pop() || '';
      // Remove any query parameters for cleaner display
      const cleanFilename = filename.split('?')[0];
      
      // If the filename looks like a UUID or hash, just show "Video" and the slot
      if (/^[a-f0-9-]{10,}$/i.test(cleanFilename)) {
        return `Video (${slotId})`;
      }
      
      return cleanFilename;
    }
    return slotId; // Fallback to slot ID if no other info available
  };

  // --- Render ---
  return (
    <Tooltip title={getTooltipContent()} arrow placement="top">
      <Box
        onClick={() => onClick(slotId)}
        sx={{
          ...style, // Apply positioning styles
          border: slotId.startsWith('tease_shot') ? 'none' : '2px solid white',
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden', // Hide video overflow
          color: 'white',
        }}
      >
        {s3Url ? (
          <video
            ref={videoRef}
            src={s3Url}
            data-slot-id={slotId} // Add data attribute for slot identification
            muted // Autoplay usually requires muted
            playsInline // Important for iOS
            preload="metadata" // Load enough to get duration and seek
            style={{
                width: slotId.startsWith('tease_shot') ? '90%' : '100%',
                height: slotId.startsWith('tease_shot') ? '90%' : '100%',
                objectFit: 'cover', // Cover the container to fill frame
                display: error ? 'none' : 'block', // Hide video on error
                marginLeft: slotId.startsWith('tease_shot') ? '-35px' : '0',
                // Apply horizontal positioning using object-position (works well for horizontal)
                objectPosition: currentFormat === 'vertical' && (horizontalPosition !== undefined || verticalPosition !== undefined)
                  ? `${50 + (horizontalPosition || 0)}% ${50 + (verticalPosition || 0)}%`  // Convert -50 to +50 range to 0% to 100%
                  : 'center center',
                // Apply positioning using transform
                transform: currentFormat === 'horizontal' && slotId.startsWith('tease_shot')
                  ? 'translateX(-50px)' // Hardcoded 50px left shift for tease horizontal videos only
                  : (verticalPosition !== undefined && verticalPosition !== 0)
                    ? `translateY(${verticalPosition}%)`  // Only vertical translation for vertical format
                    : 'translateY(0%)',
                // Debug log for positioning
                ...(slotId.startsWith('tease_shot') ? (() => {
                  console.log(`[${slotId}] Hybrid Positioning Debug:`, {
                    currentFormat,
                    horizontalPosition,
                    verticalPosition,
                    objectPosition: currentFormat === 'vertical' && (horizontalPosition !== undefined || verticalPosition !== undefined)
                      ? `${50 + (horizontalPosition || 0)}% ${50 + (verticalPosition || 0)}%`
                      : 'center center',
                    transform: (verticalPosition !== undefined && verticalPosition !== 0)
                      ? `translateY(${verticalPosition}%)`
                      : 'translateY(0%)'
                  });
                  return {};
                })() : {}),
                transition: 'object-position 0.2s ease, transform 0.2s ease' // Smooth transition when adjusting
            }}
          />
        ) : null}
        {(!s3Url || error) && (
          <Typography 
            variant="body2" 
            component="span" 
            sx={{ 
              fontWeight: 'bold',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.8)',
              marginLeft: slotId.startsWith('tease_shot') ? '-80px' : '0'
            }}
          >
            click to add video
          </Typography>
        )}
        
        {/* Tease border overlay for tease shots */}
        {slotId.startsWith('tease_shot') && teaseBorderUrl && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${teaseBorderUrl})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
        )}

        {/* Tease shot number overlay */}
        {slotId.startsWith('tease_shot') && (
          <Box
            sx={{
              position: 'absolute',
              top: '6px',
              left: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              pointerEvents: 'none',
              zIndex: 2
            }}
          >
            {slotId.replace('tease_shot', '')}
          </Box>
        )}

        {/* Progress bar showing position within loop duration */}
        {s3Url && isPlaying && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '3px',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 3
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${(currentLoopTime / videoDuration) * 100}%`,
                backgroundColor: isPreviewMode ? '#ff0000' : '#fff',
                transition: 'width 0.1s ease-out',
              }}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}; 